import type { SearchFilters, ServiceRange } from '../types';
import type { UrlLocationQuery } from './resolveUrlLocation';

/**
 * Provider search filters that a link can set via the URL query string.
 *
 * Only these keys are read. `excludeRole`, `providerType`, pagination and
 * lat/lng/radius are deliberately not accepted: `excludeRole` is forced by the
 * viewer's role (FR-124), and the backend's SP search ignores lat/lng/radius,
 * so accepting them would look like a filter that silently does nothing.
 * Location is given by id (`countyId`) or by name (`county`, `city`, `state`,
 * with `stateCode` kept as an alias of `state`); see resolveUrlLocation.
 */
const URL_FILTER_KEYS = [
  'providerName',
  'categoryIds',
  'specialtyIds',
  'serviceRanges',
  'countyId',
  'county',
  'city',
  'state',
  'stateCode',
] as const;

// Caps mirror backend/src/features/search/dto/search.dto.ts so a link can never
// produce a 400 from the search endpoint.
const MAX_PROVIDER_NAME_LENGTH = 200;
const MAX_ID_LENGTH = 64;
const MAX_IDS = 50;
const MAX_PLACE_NAME_LENGTH = 100;
const SERVICE_RANGES: readonly ServiceRange[] = ['IN_HOME', 'VIRTUAL', 'IN_OFFICE'];

export type UrlFilters = Pick<
  SearchFilters,
  'providerName' | 'categoryIds' | 'specialtyIds' | 'serviceRanges'
> & { location?: UrlLocationQuery };

export type UrlTaxonomy = {
  categories: ReadonlyArray<{ id: string }>;
  specialties: ReadonlyArray<{ id: string }>;
};

export const hasUrlFilters = (params: URLSearchParams): boolean =>
  URL_FILTER_KEYS.some((key) => params.has(key));

export const stripUrlFilters = (params: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(params);
  URL_FILTER_KEYS.forEach((key) => next.delete(key));
  return next;
};

/**
 * Writes the current filters into the URL (the inverse of parseUrlFilters), so
 * the address bar is always a shareable link to this search. Location is
 * written by id (`countyId`, else `state`). Unrelated params are kept.
 */
export const writeUrlFilters = (
  params: URLSearchParams,
  filters: SearchFilters,
): URLSearchParams => {
  const next = stripUrlFilters(params);
  if (filters.providerName) next.append('providerName', filters.providerName);
  filters.categoryIds?.forEach((id) => next.append('categoryIds', id));
  filters.specialtyIds?.forEach((id) => next.append('specialtyIds', id));
  filters.serviceRanges?.forEach((range) => next.append('serviceRanges', range));
  if (filters.countyId) next.append('countyId', filters.countyId);
  else if (filters.stateCode) next.append('state', filters.stateCode);
  return next;
};

const withoutUndefined =<T extends object>(value: T): T =>
  Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;

const readText = (params: URLSearchParams, key: string, maxLength: number) => {
  const text = params.get(key)?.trim();
  return text && text.length <= maxLength ? text : undefined;
};

const readIds = (params: URLSearchParams, key: string): string[] | undefined => {
  const ids = [
    ...new Set(params.getAll(key).filter((id) => id.length > 0 && id.length <= MAX_ID_LENGTH)),
  ];
  return ids.length > 0 ? ids : undefined;
};

/**
 * Reads the supported filters from the URL, dropping malformed values.
 * Existence is checked later by {@link resolveUrlTaxonomy} and resolveUrlLocation.
 */
export const parseUrlFilters = (params: URLSearchParams): UrlFilters => {
  const serviceRanges = [
    ...new Set(
      params
        .getAll('serviceRanges')
        .filter((range): range is ServiceRange => SERVICE_RANGES.includes(range as ServiceRange)),
    ),
  ];
  const location = withoutUndefined<UrlLocationQuery>({
    countyId: readText(params, 'countyId', MAX_ID_LENGTH),
    county: readText(params, 'county', MAX_PLACE_NAME_LENGTH),
    city: readText(params, 'city', MAX_PLACE_NAME_LENGTH),
    state:
      readText(params, 'state', MAX_PLACE_NAME_LENGTH) ??
      readText(params, 'stateCode', MAX_PLACE_NAME_LENGTH),
  });

  return withoutUndefined<UrlFilters>({
    providerName: readText(params, 'providerName', MAX_PROVIDER_NAME_LENGTH),
    categoryIds: readIds(params, 'categoryIds'),
    specialtyIds: readIds(params, 'specialtyIds'),
    serviceRanges: serviceRanges.length > 0 ? serviceRanges : undefined,
    location: Object.keys(location).length > 0 ? location : undefined,
  });
};

const keepKnownIds = (
  ids: string[] | undefined,
  known: ReadonlyArray<{ id: string }>,
): string[] | undefined => {
  if (!ids) return undefined;
  const knownIds = new Set(known.map((item) => item.id));
  const kept = ids.filter((id) => knownIds.has(id)).slice(0, MAX_IDS);
  return kept.length > 0 ? kept : undefined;
};

/** Drops taxonomy ids that don't exist. Location is resolved separately. */
export const resolveUrlTaxonomy = (
  parsed: UrlFilters,
  taxonomy: UrlTaxonomy,
): Partial<SearchFilters> =>
  withoutUndefined<Partial<SearchFilters>>({
    providerName: parsed.providerName,
    categoryIds: keepKnownIds(parsed.categoryIds, taxonomy.categories),
    specialtyIds: keepKnownIds(parsed.specialtyIds, taxonomy.specialties),
    serviceRanges: parsed.serviceRanges,
  });
